import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getTenantDefaultFee } from '$constants/SimeraOperations';
// import SPKCandidatesPositionLayout from '$components/Positions/SPKCandidatesPositionPage/SPKCandidatesPositionLayout/SPKCandidatesPositionLayout';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import ActiveValues from '$constants/ActiveValues';
import SPKPositionBar from '$components/Positions/SPKCandidatesPositionPage/SPKPositionBar/SPKPositionBar';
import ExportCandidatePositionData from '$components/Positions/SPKCandidatesPositionPage/ExportCandidatePositionData/ExportCandidatePositionData';
import SPKCardSkeleton from '$components/SPKCardSkeleton/SPKCardSkeleton';
import getEmptyResultsText from '$utils/getEmptyResultsText';
import EmptyState from '$components/Positions/EmptyState/EmptyState';
import SPKFullProfileIframeModal from '$components/Positions/SPKCandidatesPositionPage/SPKFullProfileIframeModal/SPKFullProfileIframeModal';
import SPKInterviewConfirmationModal from '$components/Positions/SPKCandidatesPositionPage/SPKInterviewConfirmationModal/SPKInterviewConfirmationModal';
import SPKNotAFitConfirmationModal from '$components/Positions/SPKCandidatesPositionPage/SPKNotAFitConfirmationModal/SPKNotAFitConfirmationModal';
import SPKNotAFitFeedbackModal from '$components/Positions/SPKCandidatesPositionPage/SPKNotAFitFeedbackModal/SPKNotAFitFeedbackModal';
import { CalendarModal } from '$components/Employers/CalendarModal/CalendarModal';
import SPKInterviewRequestedSuccessInfoModal from '$components/Positions/SPKCandidatesPositionPage/SPKInterviewRequestedSuccessInfoModal/SPKInterviewRequestedSuccessInfoModal';
import { singlePositionSlice } from '$store/modules/position/position.reducer';
import SPKEmployerPositionCandidateCard from '$components/Positions/SPKCandidatesPositionPage/SPKEmployerPositionCandidateCard/SPKEmployerPositionCandidateCard';
import { Virtuoso } from 'react-virtuoso';
import ExportShortlistCsv from './SPKCandidatesPositionPage/ExportShortlistCsv/ExportShortlistCsv';

import MatchTypes from '../../constants/MatchValues';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ReactComponent as BackArrow } from '../../icons/ArrowBackInternal.svg';
import { ReactComponent as RefreshIcon } from '../../icons/RefreshIcon.svg';
import {
  candidatesApi,
  // useGetCandidatesByPositionIdQuery,
  useLazyFindCandidateQuery,
} from '../../store/modules/candidates';
import { useLoadOnePositionQuery } from '../../store/modules/companies';
import { CandidateData } from '../../types/candidates';
import CandidatesSearchFilters from '../../types/candidatesPaginatedSearch';
import Button from '../Buttons/Button';
import { Filters } from '../Internal/Positions/Filters';
import classes from './SinglePosition.module.css';
import GetCandidateDataByMultipleFetch from './utils/GetCandidateDataByMultipleFetch';
import SearchFilter from './utils/SearchFilter';
import AddCandidatesIntoPosition from './AddCandidatesIntoPosition/AddCandidatesIntoPosition';

const SinglePositionPage: FC = () => {
  const { positionId } = useParams();
  const [candidates, setCandidates] = useState<CandidateData[]>([]);
  const [positionCandidates, setPositionCandidates] = useState<CandidateData[]>(
    []
  );
  const [findCandidatesResult, setFindCandidatesResult] = useState<
    CandidateData[]
  >([]);
  const [t] = useTranslation('global');
  const [searchParams] = useSearchParams();
  const [candidatesPositionCount, setCandidatesPositionCount] = useState({
    new: 0,
    shortlisted: 0,
    declined: 0,
    requestInterview: 0,
    hired: 0,
    offerSent: 0,
    interested: 0,
  });

  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [filteredData, setFilteredData] = useState(candidates);
  const lastSearchTotalItems = useRef(0);
  const lastSearchItems = useRef('');
  const navigate = useNavigate();
  const cps = (searchParams.get('cps') ?? ActiveValues.New) as ActiveValues;

  const { data: position, isLoading: isLoadingPosition } =
    useLoadOnePositionQuery({
      id: positionId,
    });

  // const {
  //   // data: candidatesApi,
  //   isLoading,
  //   isFetching,
  //   refetch,
  // } = useGetCandidatesByPositionIdQuery(
  //   {
  //     positionId: parseInt(positionId!, 10),
  //   },
  //   { refetchOnMountOrArgChange: true }
  // );

  const hydrationFlags = useMemo(
    () => ({
      wantsEducation: !!searchParams.get('EducationLevel'),
      wantsLanguages: !!searchParams.get('Language'),
      wantsTags: !!searchParams.get('AdditionalTags'),
      wantsEnglish: !!searchParams.get('EnglishLevel'),
      wantsInterviewStatus: false,
    }),
    [searchParams]
  );

  const { data, isLoading, isLoadingActiveStatus, refetch } =
    GetCandidateDataByMultipleFetch(position, hydrationFlags, cps);

  const [findCandidates] = useLazyFindCandidateQuery();

  const dispatch = useAppDispatch();
  const {
    Country: selectedCountryFilter,
    isOpenInterviewCalendarModal: openInterviewCalendarModal,
    chosenCandidate: selectedCandidate,
  } = useAppSelector((state) => {
    const { Country } = state.internalFilter;
    const {
      openInterviewCalendarModal: isOpenInterviewCalendarModal,
      selectedCandidate: chosenCandidate,
    } = state.position;
    return { Country, isOpenInterviewCalendarModal, chosenCandidate };
  });

  const filters = useMemo((): CandidatesSearchFilters => {
    const yearsOfExperienceQuery = searchParams.get('YearsOfExperience') || '0';
    const yearsOfExperience = parseInt(yearsOfExperienceQuery, 10) || null;

    const maxSalaryQuery = searchParams.get('MaxSalary') || '0';
    const maxSalary = parseInt(maxSalaryQuery, 10) || null;

    setPage(1);
    setHasNextPage(false);
    return {
      cps,
      matchSkill: searchParams.get('SkillsMatch') ?? MatchTypes.ALL,
      matchAdditionalTags:
        searchParams.get('AdditionalTagsMatch') ?? MatchTypes.ALL,
      additionalTags: searchParams.get('AdditionalTags')?.split(','),
      education: searchParams.get('EducationLevel')?.split(','),
      fileCategories: searchParams.get('Files')?.split(','),
      language: searchParams.get('Language'),
      skills: searchParams.get('Skills')?.split(','),
      englishLevel: searchParams.get('EnglishLevel')?.split(','),
      positionId: parseInt(positionId ?? '0', 10),
      id:
        parseInt(searchParams.get('Id') ?? '0', 10) === 0
          ? null
          : parseInt(searchParams.get('Id') ?? '0', 10),
      workExperience: searchParams.get('PreviousExperience')?.split(','),
      maxSalary,
      query: searchParams.get('Query') ?? '',
      yearsOfExperience,
      name: searchParams.get('Name') ?? null,
      email: searchParams.get('Email') ?? null,
      country: selectedCountryFilter,
    };
  }, [cps, positionId, searchParams, selectedCountryFilter]);

  const reloadCandidates = useCallback(() => {
    dispatch(candidatesApi.util.resetApiState());
    setCandidates([]);
    setPage(1);
    refetch();
  }, [dispatch, refetch]);

  const searchCandidate = useCallback(
    async (findParams: Parameters<typeof findCandidates>[0]) => {
      setIsLoadingSearch(true);
      findCandidates(findParams)
        .then((findCandidateData) => {
          setIsLoadingSearch(false);
          setHasNextPage(!!findCandidateData?.data?.next);
          setFindCandidatesResult((prev) => {
            const resultData = findCandidateData?.data?.data || [];
            return [...prev, ...resultData].reduce(
              (previous: CandidateData[], actual: CandidateData) => {
                const isNewCandidate = !previous.some(
                  (singleCandidate: CandidateData) => {
                    return singleCandidate.Id === actual.Id;
                  }
                );

                if (isNewCandidate) previous.push(actual);
                return previous;
              },
              []
            );
          });
        })
        .catch(() => {
          setIsLoadingSearch(false);
        });
    },
    [findCandidates]
  );

  const onNextPage = useCallback(() => {
    if (hasNextPage && !isLoadingSearch) {
      setPage((prev) => prev + 1);
    }
  }, [hasNextPage, isLoadingSearch]);

  const applyFilters = useMemo(() => {
    // reset lastSearchTotalItems, 25 its the default return value for the first page, if its less it should be a new search
    if (candidates.length <= 25) lastSearchTotalItems.current = 0;
    return SearchFilter(filters, candidates);
  }, [filters, candidates]);

  const handleBackToPositions = () => {
    const stored = sessionStorage.getItem('positionsFilters');
    if (stored) {
      navigate(`/internal-dashboard?${stored}`, { replace: true });
    } else {
      navigate('/internal-dashboard', { replace: true });
    }
  };

  const isLoadingWholePositionPage = useMemo(() => {
    return (
      filteredData.length === 0 &&
      ((cps === ActiveValues.find && isLoadingSearch) ||
        (cps !== ActiveValues.find && isLoadingActiveStatus))
    );
  }, [cps, filteredData.length, isLoadingActiveStatus, isLoadingSearch]);

  const emptyResultsText = useMemo(() => {
    return getEmptyResultsText(cps, t);
  }, [cps, t]);

  const virtuosoComponents = useMemo(() => {
    return {
      Item: ({ children, ...props }: any) => (
        <div {...props} className="mb-4 last:mb-0">
          {children}
        </div>
      ),
      Footer: () => {
        if (isLoadingSearch)
          return (
            <div className="mt-4 animate-pulse">
              <SPKCardSkeleton
                // eslint-disable-next-line react/no-array-index-key
                key="SPKCardSkeleton_LoadingSearch"
                height="h-56"
                className="flex-grow bg-[#EBE8DD] rounded-2xl"
              />
            </div>
          );
        return null;
      },
    };
  }, [isLoadingSearch]);

  useEffect(() => {
    setFilteredData(applyFilters);
  }, [applyFilters]);

  useEffect(() => {
    dispatch(singlePositionSlice.actions.setAllCandidates(filteredData));
  }, [dispatch, filteredData]);

  // Candidates arrive as they load, so the list fills in progressively
  useEffect(() => {
    if (data) {
      setPositionCandidates(data);
    }
  }, [data]);

  useEffect(() => {
    if (cps === ActiveValues.find) setCandidates(findCandidatesResult);
    else setCandidates(positionCandidates);
  }, [cps, positionCandidates, findCandidatesResult]);

  useEffect(() => {
    const findParamsLength = Array.from(searchParams.keys()).length;
    if (cps === ActiveValues.find) {
      const searchParamsObj = {
        ...Object.fromEntries(searchParams.entries()),
        page,
      };
      const stringifiedSearchParams = JSON.stringify(searchParamsObj);
      const isNewSearch = lastSearchItems.current !== stringifiedSearchParams;
      if (isNewSearch) {
        lastSearchItems.current = stringifiedSearchParams;
        // If search for a new term
        if (searchParamsObj.page === 1) setFindCandidatesResult([]);
        if (findParamsLength > 1) {
          searchCandidate(searchParamsObj);
        } else {
          setCandidates([]);
        }
      }
    } else {
      setCandidates(positionCandidates);
    }
  }, [cps, searchParams, positionCandidates, searchCandidate, page]);

  useEffect(() => {
    if (position?.CandidatesPositionStatus) {
      const candidatesCount = position?.CandidatesPositionStatus?.reduce(
        (acc, curr) => {
          acc[curr.Status] += 1;

          return acc;
        },
        {
          new: 0,
          shortlisted: 0,
          declined: 0,
          requestInterview: 0,
          hired: 0,
          offerSent: 0,
          interested: 0,
        }
      );
      setCandidatesPositionCount(candidatesCount);
    }
  }, [position?.CandidatesPositionStatus]);

  useEffect(() => {
    if (!(isLoading || isLoadingSearch)) {
      if (filteredData.length < 3) {
        // 3 items to make virtuoso have scroll bar and enable atBottomStateChange trigger
        onNextPage();
      } else if (
        // save last filtered result
        lastSearchTotalItems.current === 0 ||
        lastSearchTotalItems.current !== filteredData.length
      ) {
        lastSearchTotalItems.current = filteredData.length;
      } else if (lastSearchTotalItems.current === filteredData.length) {
        // if parent component sent new data and the new whole set didnt aggregate items, call again next page
        onNextPage();
      }
    }
  }, [filteredData.length, isLoading, isLoadingSearch, onNextPage]);

  const positionFeeAmount = useMemo(
    () => position?.FeeAmount ?? getTenantDefaultFee(position?.Tenant?.Name),
    [position?.FeeAmount, position?.Tenant?.Name]
  );

  return (
    <div className={classes.PositionWrapper} data-testid="positionWrapper">
      <div className={`${classes.Container} py-4`}>
        <div className="flex flex-col">
          <Button
            text={t('CandidatesTable.Previous')}
            showText
            customStyles={{
              color: '#079E88',
              alignSelf: 'flex-start',
              border: '1px solid #079E88',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              padding: '0.5rem 1rem',
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'flex-start',
            }}
            textStyles={{
              marginLeft: '30px',
            }}
            icon={BackArrow}
            iconStyles={{ fill: '#079E88' }}
            onClick={handleBackToPositions}
            styling="w-full"
          />
          <Filters />
        </div>
        <div className={classes.PositionMainContainer}>
          <SPKPositionBar
            isLoadingPosition={isLoadingPosition}
            position={position as PositionInternalDto}
            cps={cps}
            candidatesPositionCount={candidatesPositionCount}
            showInterestedToEmployer={
              position?.ShowInterestedToEmployer || false
            }
          />
          <div className="w-full flex flex-row justify-end gap-4">
            <AddCandidatesIntoPosition positionId={positionId as string} />
            <div
              className={classes.refresh}
              aria-hidden="true"
              onClick={reloadCandidates}
            >
              <RefreshIcon />
            </div>
            <ExportCandidatePositionData
              candidatesData={filteredData}
              positionId={position?.Id as number}
              disabled={cps !== ActiveValues.find && isLoadingActiveStatus}
            />
            {/* What the employer downloads, for staff to check (debugging); shown for every company */}
            {cps === ActiveValues.ShortList && position && (
              <ExportShortlistCsv
                position={position as PositionInternalDto}
                candidates={filteredData}
                disabled={isLoadingActiveStatus}
                viewer="internal"
              />
            )}
          </div>

          <div
            className="!font-mulish w-full h-full max-h-full"
            data-testid="SPKCandidatePositionPage"
          >
            <div className="container px-4 lg:px-0 h-full max-h-full">
              {isLoadingWholePositionPage && (
                <div
                  className="h-full mt-2 flex flex-col gap-4"
                  data-testid="loading-page1"
                >
                  {Array.from({ length: 5 }).map((_, index) => (
                    <SPKCardSkeleton
                      // eslint-disable-next-line react/no-array-index-key
                      key={`SPKCardSkeleton_${index}`}
                      height="h-56"
                      className="flex-grow bg-[#EBE8DD] rounded-2xl"
                    />
                  ))}
                </div>
              )}
              {filteredData && filteredData.length > 0 && (
                <div className="flex flex-col gap-4 pb-8 mt-2 h-full max-h-full">
                  {/* SPK Full Profile Iframe Modal */}
                  <SPKFullProfileIframeModal />

                  {/* Not a fit confirmation modal */}
                  <SPKNotAFitConfirmationModal />

                  {/* Not a Fit Feedback Modal */}
                  <SPKNotAFitFeedbackModal
                    positionId={parseInt(positionId as string, 10)}
                  />
                  {/* Interview Confirmation Modal */}
                  <SPKInterviewConfirmationModal />

                  {openInterviewCalendarModal &&
                    selectedCandidate &&
                    selectedCandidate?.CandidatesPositionStatus &&
                    'CandidatesPositionStatus' in selectedCandidate && (
                      <CalendarModal
                        candidateId={selectedCandidate.Id}
                        setIsOpen={(value) =>
                          dispatch(
                            singlePositionSlice.actions.setOpenInterviewCalendarModal(
                              value
                            )
                          )
                        }
                        isOpen={openInterviewCalendarModal}
                        candidatePositionStatusId={
                          selectedCandidate?.CandidatesPositionStatus?.[0]?.Id
                        }
                        handleOk={() => {
                          dispatch(
                            singlePositionSlice.actions.setOpenInterviewCalendarModal(
                              false
                            )
                          );
                          dispatch(
                            singlePositionSlice.actions.setOpenInterviewRequestedModal(
                              true
                            )
                          );
                        }}
                      />
                    )}
                  {/* Interview  Requested Success Info Modal */}
                  <SPKInterviewRequestedSuccessInfoModal />

                  <div className="h-full max-h-full">
                    <Virtuoso
                      endReached={onNextPage}
                      totalCount={filteredData.length}
                      style={{
                        marginBottom: '10px',
                        height: '100%',
                      }}
                      components={virtuosoComponents}
                      itemContent={(index: number) => {
                        const candidate = filteredData[index];
                        return (
                          <SPKEmployerPositionCandidateCard
                            key={`PositionCandidate-${candidate.Id}`}
                            candidate={candidate}
                            position={position as PositionInternalDto}
                            activeValue={cps}
                            feeAmount={positionFeeAmount}
                            showSalaryRange
                          />
                        );
                      }}
                    />
                  </div>
                </div>
              )}
              {filteredData &&
                filteredData.length === 0 &&
                !isLoadingActiveStatus &&
                !isLoadingSearch && (
                  <EmptyState
                    text={emptyResultsText.title}
                    description={emptyResultsText.description}
                  />
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SinglePositionPage;
